import 'src/assets/css/odq-lighthouse.scss';
import * as React from 'react';
import {LighthouseTemplateProps} from 'src/app/config/LighthouseTemplateRegistry';
import CaseDetailsSection from 'src/app/component/lighthouse/review/CaseDetailsSection';
import AuditChecksSection from 'src/app/component/lighthouse/review/AuditChecksSection';
import CaseHistorySection from 'src/app/component/lighthouse/review/CaseHistorySection';
import DefectsPanel from 'src/app/component/odqAndReliability/DefectsPanel';
import OdqLinksRow from 'src/app/component/odqAndReliability/OdqLinksRow';
import StandingCard from 'src/app/component/odqAndReliability/StandingCard';

/**
 * ODQLighthouseTemplate - Review-tab layout for ODQ and Reliability cases.
 *
 * Layout only. Serves both caseSubTypes: DefectsPanel and StandingCard already branch
 * internally on caseSubType for their defect columns and score types.
 *
 *   ┌─────────────────┬──────────────────────┐
 *   │ Case Details    │ Audit Checks (capped)│
 *   │ Links           ├──────────────────────┤
 *   ├─────────────────┤ Standing             │
 *   │ Defects (grows) ├──────────────────────┤
 *   │                 │ Case History (grows) │
 *   └─────────────────┴──────────────────────┘
 */
const ODQLighthouseTemplate: React.FC<LighthouseTemplateProps> = ({
    reviewData,
    auditCheckResponses,
    onAuditCheckChange,
    onViewAttachment,
    isReadOnly
}) => (
    <div className={`odq-lh-columns${isReadOnly ? ' lighthouse-readonly' : ''}`}
         data-testid="odq-lighthouse-template">
        <div className="odq-lh-col">
            <div className="odq-lh-card">
                <CaseDetailsSection fields={reviewData.caseDetails} />
                <OdqLinksRow referenceId={reviewData.referenceId} />
            </div>
            {/* Unwrapped: DefectsPanel's root is already `.odq-card .odq-card--flex`, which
                supplies the card chrome and keeps its pagination row pinned. */}
            <DefectsPanel caseId={reviewData.reviewId}
                          caseSubType={reviewData.caseSubType}
                          transporterId={reviewData.referenceId}
                          country={reviewData.country}
                          pdSnapshot={reviewData.pdSnapshot} />
        </div>

        <div className="odq-lh-col">
            <div className="odq-lh-card odq-lh-card--scroll">
                {/* In-flight answers win over backend values so a re-render does not discard
                    what the auditor selected. */}
                <AuditChecksSection
                    fields={reviewData.auditChecks.map(f => ({
                        ...f,
                        value: auditCheckResponses[f.label] ?? f.value
                    }))}
                    onChange={onAuditCheckChange}
                />
            </div>
            <StandingCard referenceId={reviewData.referenceId}
                          caseSubType={reviewData.caseSubType} />
            <div className="odq-lh-card odq-lh-card--flex">
                <CaseHistorySection history={reviewData.history}
                                    onViewAttachment={onViewAttachment} />
            </div>
        </div>
    </div>
);

export default ODQLighthouseTemplate;
